import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  FaArrowLeft,
  FaUsers,
  FaComments,
  FaHeart,
  FaClock,
  FaNetworkWired,
  FaChartLine,
  FaRobot,
  FaUser,
} from "react-icons/fa";
import NetworkGraph from "./NetworkGraph";
import api from "../../services/api";

const COLORS = {
  pro: "#3b82f6",
  neutral: "#6b7280",
  contra: "#ef4444",
  human: "#22c55e",
  ai: "#9333ea",
};

const InstanceDetail = () => {
  const { instanceId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [networkData, setNetworkData] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [topUsers, setTopUsers] = useState([]);

  // Graph display options
  const [colorBy, setColorBy] = useState("opinion");
  const [sizeBy, setSizeBy] = useState("followers");
  const [selectedNode, setSelectedNode] = useState(null);

  const adminToken = localStorage.getItem("admin_token");

  const fetchData = useCallback(async () => {
    if (!adminToken || !instanceId) return;

    setLoading(true);
    setError(null);

    try {
      const config = {
        headers: { Authorization: `Bearer ${adminToken}` },
        // Override condition param that interceptor adds
        params: {},
      };

      const [networkRes, metricsRes, timelineRes, topUsersRes] = await Promise.all([
        api.get(`/admin/instances/${instanceId}/network`, config),
        api.get(`/admin/instances/${instanceId}/metrics`, config),
        api.get(`/admin/instances/${instanceId}/timeline`, { ...config, params: { interval: "minute" } }),
        api.get(`/admin/instances/${instanceId}/top-users`, { ...config, params: { limit: 10 } }),
      ]);

      setNetworkData(networkRes.data);
      setMetrics(metricsRes.data);
      setTimeline(timelineRes.data);
      setTopUsers(topUsersRes.data);
    } catch (err) {
      console.error("Error fetching instance data:", err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [adminToken, instanceId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleNodeClick = (node) => {
    setSelectedNode(node);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-400 hover:text-white mb-4"
          >
            <FaArrowLeft className="mr-2" /> Back
          </button>
          <div className="bg-red-900/50 text-red-200 p-4 rounded-lg">
            Error: {error}
          </div>
        </div>
      </div>
    );
  }

  // Prepare pie chart data for opinion distribution
  const opinionPieData = metrics?.users?.opinionDistribution
    ? [
        { name: "Pro", value: metrics.users.opinionDistribution.pro, color: COLORS.pro },
        { name: "Neutral", value: metrics.users.opinionDistribution.neutral, color: COLORS.neutral },
        { name: "Contra", value: metrics.users.opinionDistribution.contra, color: COLORS.contra },
      ].filter(d => d.value > 0)
    : [];

  // Prepare user type pie data
  const userTypePieData = metrics?.users
    ? [
        { name: "Human", value: metrics.users.human, color: COLORS.human },
        { name: "AI Agents", value: metrics.users.ai, color: COLORS.ai },
      ].filter(d => d.value > 0)
    : [];

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-400 hover:text-white mr-4"
            >
              <FaArrowLeft className="mr-2" /> Back
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">Instance Analytics</h1>
              <p className="text-gray-400 text-sm">{instanceId}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span
              className={`px-3 py-1 rounded-full text-sm ${
                metrics?.instance?.status === "active"
                  ? "bg-green-900 text-green-200"
                  : metrics?.instance?.status === "ready"
                  ? "bg-blue-900 text-blue-200"
                  : "bg-gray-700 text-gray-300"
              }`}
            >
              {metrics?.instance?.status || "Unknown"}
            </span>
          </div>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <MetricCard
            icon={<FaUsers />}
            label="Total Users"
            value={metrics?.users?.total || 0}
            subtext={`${metrics?.users?.human || 0} human, ${metrics?.users?.ai || 0} AI`}
            color="blue"
          />
          <MetricCard
            icon={<FaComments />}
            label="Messages"
            value={metrics?.content?.messages?.total || 0}
            subtext={`${metrics?.content?.messages?.byAI || 0} by AI`}
            color="green"
          />
          <MetricCard
            icon={<FaHeart />}
            label="Total Likes"
            value={metrics?.content?.messages?.totalLikes || 0}
            subtext={`${metrics?.engagement?.avgLikesPerPost || 0} avg/post`}
            color="pink"
          />
          <MetricCard
            icon={<FaClock />}
            label="Comments"
            value={metrics?.content?.comments?.total || 0}
            subtext={`${metrics?.engagement?.avgCommentsPerPost || 0} avg/post`}
            color="purple"
          />
        </div>

        {/* Network Metrics */}
        {networkData?.metrics && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <SmallMetric label="Nodes" value={networkData.metrics.nodeCount} />
            <SmallMetric label="Edges" value={networkData.metrics.edgeCount} />
            <SmallMetric label="Density" value={networkData.metrics.density} />
            <SmallMetric label="Reciprocity" value={networkData.metrics.reciprocity} />
            <SmallMetric label="Homophily" value={networkData.metrics.homophily} />
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Network Graph - Takes 2 columns */}
          <div className="lg:col-span-2 bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center">
                <FaNetworkWired className="mr-2" /> Social Network
              </h2>
              <div className="flex space-x-4">
                <select
                  value={colorBy}
                  onChange={(e) => setColorBy(e.target.value)}
                  className="bg-gray-700 text-white text-sm rounded px-2 py-1"
                >
                  <option value="opinion">Color by Opinion</option>
                  <option value="type">Color by Type</option>
                  <option value="orientation">Color by Orientation</option>
                </select>
                <select
                  value={sizeBy}
                  onChange={(e) => setSizeBy(e.target.value)}
                  className="bg-gray-700 text-white text-sm rounded px-2 py-1"
                >
                  <option value="followers">Size by Followers</option>
                  <option value="activity">Size by Activity</option>
                  <option value="equal">Equal Size</option>
                </select>
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 mb-4 text-sm">
              {colorBy === "opinion" && (
                <>
                  <LegendItem color={COLORS.contra} label="Contra (-)" />
                  <LegendItem color={COLORS.neutral} label="Neutral" />
                  <LegendItem color={COLORS.pro} label="Pro (+)" />
                </>
              )}
              {colorBy === "type" && (
                <>
                  <LegendItem color={COLORS.human} label="Human" />
                  <LegendItem color={COLORS.ai} label="AI Agent" />
                </>
              )}
              {colorBy === "orientation" && (
                <>
                  <LegendItem color="#3b82f6" label="Ingroup" />
                  <LegendItem color="#a3a3a3" label="Neutral" />
                  <LegendItem color="#ef4444" label="Outgroup" />
                </>
              )}
            </div>

            <NetworkGraph
              nodes={networkData?.nodes || []}
              edges={networkData?.edges || []}
              colorBy={colorBy}
              sizeBy={sizeBy}
              onNodeClick={handleNodeClick}
              width={700}
              height={450}
            />

            {/* Selected Node Info */}
            {selectedNode && (
              <div className="mt-4 p-3 bg-gray-700 rounded-lg">
                <h3 className="font-semibold text-white">{selectedNode.username}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-sm text-gray-300">
                  <span>Opinion: {(selectedNode.opinionPosition || 0).toFixed(2)}</span>
                  <span>Followers: {selectedNode.followerCount || 0}</span>
                  <span>Following: {selectedNode.followingCount || 0}</span>
                  <span>Type: {selectedNode.artificial ? "AI" : "Human"}</span>
                </div>
              </div>
            )}
          </div>

          {/* Side Panel */}
          <div className="space-y-6">
            {/* User Distribution */}
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-white font-semibold mb-4">User Distribution</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="h-28 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                        <Pie
                          data={userTypePieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={20}
                          outerRadius={35}
                          dataKey="value"
                        >
                          {userTypePieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ backgroundColor: "#1f2937", border: "none", borderRadius: "4px", fontSize: "12px" }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-gray-400 text-xs mt-1">By Type</p>
                  <div className="flex justify-center gap-2 mt-1 text-xs">
                    <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-green-500 mr-1"></span>{metrics?.users?.human || 0}</span>
                    <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-purple-500 mr-1"></span>{metrics?.users?.ai || 0}</span>
                  </div>
                </div>
                <div className="text-center">
                  <div className="h-28 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                        <Pie
                          data={opinionPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={20}
                          outerRadius={35}
                          dataKey="value"
                        >
                          {opinionPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ backgroundColor: "#1f2937", border: "none", borderRadius: "4px", fontSize: "12px" }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-gray-400 text-xs mt-1">By Opinion</p>
                  <div className="flex justify-center gap-2 mt-1 text-xs">
                    <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-red-500 mr-1"></span>{metrics?.users?.opinionDistribution?.contra || 0}</span>
                    <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-gray-500 mr-1"></span>{metrics?.users?.opinionDistribution?.neutral || 0}</span>
                    <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-blue-500 mr-1"></span>{metrics?.users?.opinionDistribution?.pro || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Users */}
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-white font-semibold mb-4">Most Active Users</h3>
              <div className="space-y-2">
                {topUsers.slice(0, 5).map((user, index) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between text-sm"
                  >
                    <div className="flex items-center">
                      <span className="text-gray-500 w-5">{index + 1}.</span>
                      {user.artificial ? (
                        <FaRobot className="text-purple-400 mr-2" />
                      ) : (
                        <FaUser className="text-green-400 mr-2" />
                      )}
                      <span className="text-white">{user.username}</span>
                    </div>
                    <span className="text-gray-400">
                      {user.totalActivity} actions
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Timeline */}
        {timeline?.timeline?.length > 0 && (
          <div className="mt-6 bg-gray-800 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-white flex items-center mb-4">
              <FaChartLine className="mr-2" /> Activity Timeline
            </h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeline.timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="timestamp"
                    stroke="#9ca3af"
                    tick={{ fill: "#9ca3af", fontSize: 10 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getHours()}:${date.getMinutes().toString().padStart(2, "0")}`;
                    }}
                  />
                  <YAxis stroke="#9ca3af" tick={{ fill: "#9ca3af" }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1f2937",
                      border: "none",
                      borderRadius: "8px",
                    }}
                    labelStyle={{ color: "#fff" }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="messages"
                    stroke="#3b82f6"
                    name="Messages"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="comments"
                    stroke="#22c55e"
                    name="Comments"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="likes"
                    stroke="#ec4899"
                    name="Likes"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* AI vs Human Activity */}
            <div className="mt-4 h-48">
              <h3 className="text-white text-sm font-semibold mb-2">AI vs Human Activity</h3>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={timeline.timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="timestamp"
                    stroke="#9ca3af"
                    tick={{ fill: "#9ca3af", fontSize: 10 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getHours()}:${date.getMinutes().toString().padStart(2, "0")}`;
                    }}
                  />
                  <YAxis stroke="#9ca3af" tick={{ fill: "#9ca3af" }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1f2937",
                      border: "none",
                      borderRadius: "8px",
                    }}
                  />
                  <Legend />
                  <Bar dataKey="messagesByHuman" stackId="messages" fill="#22c55e" name="Human Posts" />
                  <Bar dataKey="messagesByAI" stackId="messages" fill="#9333ea" name="AI Posts" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper Components
const MetricCard = ({ icon, label, value, subtext, color }) => {
  const colorClasses = {
    blue: "bg-blue-900/50 text-blue-400",
    green: "bg-green-900/50 text-green-400",
    pink: "bg-pink-900/50 text-pink-400",
    purple: "bg-purple-900/50 text-purple-400",
  };

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-sm">{label}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
          {subtext && <p className="text-gray-500 text-xs">{subtext}</p>}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>{icon}</div>
      </div>
    </div>
  );
};

const SmallMetric = ({ label, value }) => (
  <div className="bg-gray-800 rounded-lg p-3 text-center">
    <p className="text-gray-400 text-xs">{label}</p>
    <p className="text-lg font-semibold text-white">
      {typeof value === "number" ? value.toFixed(3) : value}
    </p>
  </div>
);

const LegendItem = ({ color, label }) => (
  <div className="flex items-center">
    <div
      className="w-3 h-3 rounded-full mr-2"
      style={{ backgroundColor: color }}
    />
    <span className="text-gray-400 text-xs">{label}</span>
  </div>
);

export default InstanceDetail;
